import { stripTrailingSlashes } from "../utils/stripTrailingSlashes";

const ENV = import.meta.env;

export const DIRECT_FLOW_CONFIG = {
  proxyBaseUrl: ENV.VITE_ESTIA_PROXY_BASE_URL || "/api/v1",
  callbackUrl: ENV.VITE_ESTIA_CALLBACK_URL || "",
  currency: ENV.VITE_ESTIA_CURRENCY || "EUR",
  deviceType: ENV.VITE_ESTIA_DEVICE_TYPE || "WEB",
  defaultAmount: Number(ENV.VITE_ESTIA_DEFAULT_AMOUNT || "10.50"),
  expirySeconds: Number(ENV.VITE_CHECKOUT_EXPIRY_SECONDS || "180"),
};

export function hasDirectFlowConfig(): boolean {
  return Boolean(DIRECT_FLOW_CONFIG.proxyBaseUrl);
}

type BackendErrorPayload = {
  error?: string;
  message?: string;
  correlationId?: string;
};

function formatIntentError(status: number, payloadText: string): string {
  if (status === 409) {
    return "Maximum concurrent payment intents reached. Please wait for existing intents to expire and try again.";
  }

  try {
    const payload = JSON.parse(payloadText) as BackendErrorPayload;
    if (payload.error === "PAYMENT_ERROR" && payload.message?.includes("403")) {
      return "Payment setup is not authorized for this merchant/key (Estia 403). Please ask backend for a valid API key + merchant mapping for this environment.";
    }

    if (payload.message) {
      return payload.correlationId
        ? `${payload.message} (correlationId: ${payload.correlationId})`
        : payload.message;
    }
  } catch {
    // keep raw text fallback
  }

  return `Estia intent failed (${status}): ${payloadText}`;
}

export async function createDirectEstiaIntent(
  amount: number,
  transactionId?: string
): Promise<{ intentId: string; trackingId: string }> {
  const targetUrl = `${stripTrailingSlashes(DIRECT_FLOW_CONFIG.proxyBaseUrl)}/estia/intents`;
  let response: Response;

  try {
    response = await fetch(targetUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        amount,
        currency: DIRECT_FLOW_CONFIG.currency,
        transactionId,
        callbackUrl: DIRECT_FLOW_CONFIG.callbackUrl,
        deviceType: DIRECT_FLOW_CONFIG.deviceType,
      }),
    });
  } catch {
    throw new Error(
      `Failed to reach backend proxy at ${targetUrl}. Check backend is running and CORS allows this frontend origin.`
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(formatIntentError(response.status, errorText));
  }

  return (await response.json()) as { intentId: string; trackingId: string };
}

export async function getDirectEstiaIntentStatus(
  trackingId: string
): Promise<{ status?: string }> {
  const targetUrl = `${stripTrailingSlashes(DIRECT_FLOW_CONFIG.proxyBaseUrl)}/estia/intents/${trackingId}/status`;
  const response = await fetch(targetUrl);
  if (!response.ok) {
    throw new Error(`Status check failed (${response.status})`);
  }
  return (await response.json()) as { status?: string };
}
